import React, { useState } from 'react';

import { EvidencePostureChip, formatEvidenceDateTime } from '../evidencePosture';
import {
  portfolioReliabilityStatus,
  reliabilityVerdictChipClassName,
  rollupReliabilityHelperCopy,
  rollupReliabilitySummaryItems,
} from '../reliability';

function totalSystemsNeedingAttention(systems) {
  return systems.filter((system) => (system.portfolio_summary?.active_finding_count || 0) > 0).length;
}

function totalSystemsNeedingValidation(systems) {
  return systems.filter((system) => (system.portfolio_summary?.workflows_requiring_validation_count || 0) > 0).length;
}

function totalSystemsWithFreshnessRisk(systems) {
  return systems.filter((system) => (system.portfolio_summary?.evidence_posture?.state || 'unknown') !== 'current').length;
}

const GUIDED_ALPHA_GUIDANCE_KEY = 'agentic-blue.guided-alpha-onboarding.hidden';

function readGuidanceHidden() {
  try {
    return window.localStorage.getItem(GUIDED_ALPHA_GUIDANCE_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function SystemsPortfolioView({ systems, onRequestDelete }) {
  const [guidanceHidden, setGuidanceHidden] = useState(readGuidanceHidden);
  const showFirstRunGuidance = systems.length === 0 && !guidanceHidden;

  const hideGuidance = () => {
    setGuidanceHidden(true);
    try {
      window.localStorage.setItem(GUIDED_ALPHA_GUIDANCE_KEY, 'true');
    } catch {
      return;
    }
  };

  return (
    <section className="content-grid single-column">
      <div className="panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Portfolio</p>
            <h2>Systems at a glance</h2>
            <p className="panel-copy">
              Start with readiness, then move directly into the claim, findings, validation work, and proof for each system.
            </p>
          </div>
          <a className={showFirstRunGuidance ? 'primary-button first-run-cta' : 'primary-button'} href="#/systems/onboard">
            <span className="button-icon">+</span>
            Create new system
          </a>
        </div>

        {showFirstRunGuidance ? (
          <div className="guidance-panel first-run-guidance" aria-label="First system guidance">
            <div>
              <p className="detail-kicker">How to get started</p>
              <div className="guidance-steps" aria-label="Create system, upload trace, review findings">
                <span>Create system</span>
                <span aria-hidden="true">→</span>
                <span>Upload trace</span>
                <span aria-hidden="true">→</span>
                <span>Review findings</span>
              </div>
              <p>Start with one representative workflow trace. Baseline and compare views become useful after the primary evidence is in.</p>
            </div>
            <button className="text-button guidance-dismiss" type="button" onClick={hideGuidance}>
              Hide guidance
            </button>
          </div>
        ) : null}

        <div className="system-summary-grid system-summary-grid-portfolio">
          <div className="summary-tile">
            <strong>{systems.length}</strong>
            <span>Tracked systems</span>
          </div>
          <div className="summary-tile">
            <strong>{totalSystemsNeedingAttention(systems)}</strong>
            <span>Systems with open findings</span>
          </div>
          <div className="summary-tile">
            <strong>{totalSystemsNeedingValidation(systems)}</strong>
            <span>Systems needing validation</span>
          </div>
          <div className="summary-tile">
            <strong>{totalSystemsWithFreshnessRisk(systems)}</strong>
            <span>Systems with stale or unknown freshness</span>
          </div>
        </div>

        <div className="stack system-grid system-grid-portfolio">
          {systems.map((system) => {
            const portfolioSummary = system.portfolio_summary || {};
            const reliabilitySummary = portfolioSummary.reliability_summary || null;
            const reliabilityItems = rollupReliabilitySummaryItems(reliabilitySummary, { hideZeroCounts: true });

            return (
              <article className="system-card system-card-portfolio" key={system.id}>
                <div className="system-card-header">
                  <div>
                    <p className="card-kicker">{system.status}</p>
                    <h3>{system.name}</h3>
                  </div>
                  <button
                    aria-label={`Delete ${system.name}`}
                    className="icon-button"
                    type="button"
                    onClick={() => onRequestDelete(system)}
                  >
                    Delete
                  </button>
                </div>

                <p>{system.description || 'No description provided yet. Add one during onboarding to improve review context.'}</p>

                <div className="system-card-topline">
                  <EvidencePostureChip posture={portfolioSummary.evidence_posture} prefix="Freshness" />
                  <span className="chip">Active findings {portfolioSummary.active_finding_count || 0}</span>
                  <span className="chip">Validation needed {portfolioSummary.workflows_requiring_validation_count || 0}</span>
                </div>

                <div className="system-card-metadata">
                  <div className="detail-card">
                    <strong>Reliability status</strong>
                    <p>{portfolioReliabilityStatus(reliabilitySummary)}</p>
                  </div>
                  <div className="detail-card">
                    <strong>Last analysis context</strong>
                    <p>Last refreshed {formatEvidenceDateTime(portfolioSummary.last_refreshed_at)}</p>
                    <p>Last analysed {formatEvidenceDateTime(portfolioSummary.evidence_posture?.last_analysed_at)}</p>
                  </div>
                </div>

                {reliabilityItems.length ? (
                  <>
                    <p className="overview-note">{rollupReliabilityHelperCopy}</p>
                    <div className="chip-row">
                      {reliabilityItems.map((item) => (
                        <span
                          aria-label={item.accessibleLabel}
                          className={reliabilityVerdictChipClassName(item.key)}
                          key={`${system.id}-${item.key}`}
                          title={item.accessibleLabel}
                        >
                          {item.label} {item.count}
                        </span>
                      ))}
                    </div>
                  </>
                ) : null}

                <div className="system-actions">
                  <a className="secondary-button action-link-button" href={`#/systems/${system.id}`}>
                    Open overview
                  </a>
                  <a className="secondary-button action-link-button" href={`#/systems/${system.id}/findings`}>
                    Review findings
                  </a>
                </div>
              </article>
            );
          })}

          {!systems.length ? (
            <div className="empty-state-card">
              <strong>No systems available yet</strong>
              <p>Create your first system, upload the primary runtime trace, then review the findings generated from that evidence.</p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
